import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { SignedImg } from "@/components/shared/SignedImg";
import { PdfViewer } from "@/components/shared/PdfTile";
import { usePhotoGestures, EdgeGlows } from "@/components/shared/usePhotoGestures";

// Celoplošný náhled fotky. Zavře se klepnutím mimo fotku, křížkem, Escape i tahem
// dolů. Nové props (title, actions, onPrev, onNext) jsou volitelné.
// Gesta (175, usePhotoGestures): fotka sleduje prst a odjede na další / předchozí,
// dva prsty nebo dvojklep (dvojklik) přiblíží, přiblížená fotka jde posouvat,
// Ctrl + kolečko přibližuje na počítači.
export function PhotoLightbox({ url, onClose, title, subtitle, actions, onPrev, onNext }) {
  const stage = useRef(null);
  const track = useRef(null);
  const { handlers, trackProps, glowRefs, flashEdge } = usePhotoGestures({
    stageRef: stage, trackRef: track, index: 0, mode: "out",
    canPrev: !!onPrev, canNext: !!onNext, onStep: (d) => (d > 0 ? onNext?.() : onPrev?.()),
    getZoomEl: () => track.current?.querySelector("[data-zoom]"),
    // Klepnutí vedle fotky zavře náhled (na fotce nic — dvojklep je zoom).
    onTap: (e) => { if (!e.target?.closest?.("[data-lightbox-img]")) onClose(); },
    onClose, resetKey: url,
  });
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); e.stopPropagation(); }
      if (e.key === "ArrowLeft") { if (onPrev) onPrev(); else flashEdge("left"); e.stopPropagation(); }
      if (e.key === "ArrowRight") { if (onNext) onNext(); else flashEdge("right"); e.stopPropagation(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, onClose, onPrev, onNext]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!url) return null;
  return createPortal(
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="photo-lightbox"
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm animate-in fade-in duration-150 motion-reduce:animate-none pointer-events-auto">
      <div {...handlers} onClick={(e) => e.stopPropagation()} className="absolute inset-0 overflow-hidden" data-testid="lightbox-stage">
        <EdgeGlows glowRefs={glowRefs} />
        <div {...trackProps} className="absolute inset-0 flex will-change-transform">
          <div className="shrink-0 w-full h-full flex items-center justify-center px-4 pt-[max(4.5rem,calc(env(safe-area-inset-top)+3.5rem))] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <div data-zoom data-lightbox-img className="origin-center" data-testid="lightbox-zoom-area">
              <SignedImg key={url} url={url} alt="Fotka z práce"
                className="block max-w-[92vw] max-h-[80vh] md:max-h-[84vh] object-contain rounded-[12px] select-none pointer-events-none animate-in fade-in duration-200 motion-reduce:animate-none" />
            </div>
          </div>
        </div>
      </div>
      <div className="absolute top-[max(1rem,env(safe-area-inset-top))] left-4 right-4 flex items-center justify-between gap-3 z-10 pointer-events-none">
        <div className="min-w-0">
          <div className="text-white/90 text-sm font-semibold truncate">{title || ""}</div>
          {subtitle && <div className="text-white/60 text-xs truncate">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-2 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
          {actions}
          <button type="button" onClick={onClose} aria-label="Zavřít"
            className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="photo-lightbox-close">
            <X size={22} />
          </button>
        </div>
      </div>
      {onPrev && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Předchozí"
          className="hidden md:grid absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white place-items-center" data-testid="lightbox-prev">
          <ChevronLeft size={24} />
        </button>
      )}
      {onNext && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Další"
          className="hidden md:grid absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white place-items-center" data-testid="lightbox-next">
          <ChevronRight size={24} />
        </button>
      )}
    </div>,
    document.body
  );
}

// Miniatura 48×48 s vlastním zvětšením po kliknutí. Nahrazuje dřívější
// <a href={resolveImg(url)} target="_blank"> — odkaz v sobě nesl podpis platný
// 10 minut, takže po chvíli otevíral jen chybovou stránku.
export function PhotoThumb({ url, testid }) {
  const [open, setOpen] = useState(false);
  // 332: dodací list může být PDF — místo rozbitého obrázku ikona, po kliknutí celá obrazovka
  if (/\.pdf($|\?)/i.test(url || "")) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} aria-label="Zvětšit PDF" title="PDF"
          className="w-12 h-12 rounded-md border border-border bg-secondary grid place-items-center text-muted-foreground" data-testid={testid}>
          <FileText size={22} aria-hidden="true" />
        </button>
        {open && <PdfViewer url={url} onClose={() => setOpen(false)} />}
      </>
    );
  }
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Zvětšit fotku"
        className="block w-12 h-12 rounded-md overflow-hidden border border-border" data-testid={testid}>
        <SignedImg url={url} alt="" thumb={48} className="w-full h-full object-cover" />
      </button>
      {open && <PhotoLightbox url={url} onClose={() => setOpen(false)} />}
    </>
  );
}
